/**
 * useUpdater — client hook for /api/admin/updater/* facade.
 *
 * Guarantees:
 *  - The reauth token lives in a useRef (memory only). Never touches
 *    localStorage/sessionStorage/cookies. Cleared after single use.
 *  - Automatic polling of /status with 2s interval when a lock is held,
 *    30s otherwise.
 *  - No secrets ever logged. Errors surface `detail` from the backend when
 *    present, otherwise a French fallback.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api/admin/updater`;

function authHeaders() {
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function extractDetail(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.detail) {
    // Control plane sometimes returns a stringified JSON in detail (proxy)
    if (typeof data.detail === "string" && data.detail.startsWith("{")) {
      try {
        const inner = JSON.parse(data.detail);
        return inner.detail || data.detail;
      } catch (e) { /* fall through */ }
    }
    return typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
  }
  return err?.message || fallback;
}

export function useUpdater({ onReauthNeeded } = {}) {
  const reauthTokenRef = useRef(null);
  const reauthExpRef = useRef(null);

  const clearReauth = useCallback(() => {
    reauthTokenRef.current = null;
    reauthExpRef.current = null;
  }, []);

  const getStatus = useCallback(async () => {
    // Safety timeout so the admin UI never hangs indefinitely if the
    // control-plane loopback (UPDATER_INTERNAL_URL) is down.
    const r = await axios.get(`${API}/status`, { headers: authHeaders(), timeout: 10000 });
    return r.data;
  }, []);

  const getHistory = useCallback(async ({ operationId, limit = 100, skip = 0 } = {}) => {
    const params = { limit, skip };
    if (operationId) params.operation_id = operationId;
    const r = await axios.get(`${API}/history`, { headers: authHeaders(), params });
    return r.data;
  }, []);

  const uploadPackage = useCallback(async ({ zipFile, manifestFile, signatureFile, onProgress }) => {
    const form = new FormData();
    form.append("package", zipFile);
    form.append("manifest", manifestFile);
    form.append("signature", signatureFile);
    const r = await axios.post(`${API}/upload`, form, {
      headers: { ...authHeaders() },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) onProgress(Math.round((evt.loaded * 100) / evt.total));
      },
    });
    return r.data;
  }, []);

  const dryRun = useCallback(async (uploadId) => {
    const r = await axios.post(`${API}/dry-run/${uploadId}`, {}, { headers: authHeaders() });
    return r.data;
  }, []);

  const requestReauth = useCallback(async ({ password, op, ref = null }) => {
    const r = await axios.post(
      `${API}/reauth`,
      { password, op, ref },
      { headers: authHeaders() }
    );
    reauthTokenRef.current = r.data.reauth_token;
    reauthExpRef.current = r.data.expires_at;
    // Auto-clear after 5 min just in case single-use never triggered
    setTimeout(() => clearReauth(), 5 * 60 * 1000);
    return { expiresAt: r.data.expires_at };
  }, [clearReauth]);

  const _consumeReauth = useCallback(() => {
    const t = reauthTokenRef.current;
    // Immediately clear so a follow-up call cannot reuse it, even if the
    // request is retried by the caller.
    reauthTokenRef.current = null;
    reauthExpRef.current = null;
    return t;
  }, []);

  const installWithReauth = useCallback(async (uploadId, typedConfirmation) => {
    const token = _consumeReauth();
    if (!token) {
      if (onReauthNeeded) onReauthNeeded("install");
      throw new Error("Ré-authentification requise.");
    }
    try {
      const r = await axios.post(
        `${API}/install/${uploadId}`,
        { typed_confirmation: typedConfirmation || null },
        { headers: { ...authHeaders(), "X-Updater-Reauth": token }, timeout: 600000 }
      );
      return r.data;
    } catch (err) {
      if (err?.response?.status === 401 && onReauthNeeded) onReauthNeeded("install");
      throw err;
    }
  }, [_consumeReauth, onReauthNeeded]);

  const rollbackWithReauth = useCallback(async ({ targetVersion = null } = {}) => {
    const token = _consumeReauth();
    if (!token) {
      if (onReauthNeeded) onReauthNeeded("rollback");
      throw new Error("Ré-authentification requise.");
    }
    try {
      const r = await axios.post(
        `${API}/rollback`,
        { target_version: targetVersion },
        { headers: { ...authHeaders(), "X-Updater-Reauth": token }, timeout: 600000 }
      );
      return r.data;
    } catch (err) {
      if (err?.response?.status === 401 && onReauthNeeded) onReauthNeeded("rollback");
      throw err;
    }
  }, [_consumeReauth, onReauthNeeded]);

  const listGitHubReleases = useCallback(async () => {
    const r = await axios.get(`${API}/github/releases`, { headers: authHeaders() });
    return r.data;
  }, []);

  const fetchGitHubRelease = useCallback(async (tag) => {
    const r = await axios.post(
      `${API}/github/fetch`,
      { tag },
      { headers: authHeaders(), timeout: 120000 }
    );
    return r.data; // { upload_id, manifest, package_sha256 }
  }, []);

  return {
    getStatus, getHistory,
    uploadPackage, dryRun,
    requestReauth, installWithReauth, rollbackWithReauth,
    listGitHubReleases, fetchGitHubRelease,
    clearReauth,
    extractDetail,
    hasReauth: () => Boolean(reauthTokenRef.current),
  };
}

/** Polls /status periodically. Fast poll (2s) when a lock is held. */
export function useUpdaterStatus() {
  const { getStatus, extractDetail } = useUpdater();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    async function fetchOnce() {
      try {
        const s = await getStatus();
        if (!cancelled) { setStatus(s); setError(null); setLoading(false); }
      } catch (err) {
        if (!cancelled) { setError(extractDetail(err, "Impossible de récupérer le statut.")); setLoading(false); }
      }
    }
    fetchOnce();
    const intervalMs = status?.lock_held_by ? 2000 : 30000;
    timer = setTimeout(() => setTick((t) => t + 1), intervalMs);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, status?.lock_held_by]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  return { status, loading, error, refresh };
}
